import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { REPORTS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, RefreshCw, BarChart3, Users, Building2, Mail, FileText, Link as LinkIcon, Play, Sparkles, ShoppingBag, TrendingUp, TrendingDown, Minus, BookOpen, Eye } from "lucide-react";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const CHANNEL_LABEL = {
  airbnb: "Airbnb",
  booking: "Booking.com",
  vrbo: "Vrbo",
  expedia: "Expedia",
  internal: "Direct",
  hostaway: "Hostaway",
  unknown: "Unknown",
};

const money = (n) => `£${(n || 0).toFixed(2)}`;

export default function Reports() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [groupBy, setGroupBy] = useState("property");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAddr, setEmailAddr] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkAddr, setLinkAddr] = useState("");
  const [linking, setLinking] = useState(false);
  const [runningMonthly, setRunningMonthly] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [upsells, setUpsells] = useState(null);
  const [upsellsOpen, setUpsellsOpen] = useState(false);
  const [guidebook, setGuidebook] = useState(null);
  const [aiDrafts, setAiDrafts] = useState(null);

  const load = () => {
    setBusy(true);
    api
      .get("/reports/owner-payout", { params: { month, group_by: groupBy === "owner" ? "owner" : undefined } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
    // Fire-and-forget preview refresh for the same month
    api.get("/reports/payout-preview", { params: { month } })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
    api.get("/reports/upsells-sales", { params: { month } })
      .then((r) => setUpsells(r.data))
      .catch(() => setUpsells(null));
    api.get("/reports/guidebook-analytics", { params: { month } })
      .then((r) => setGuidebook(r.data))
      .catch(() => setGuidebook(null));
    api.get("/reports/ai-drafts")
      .then((r) => setAiDrafts(r.data))
      .catch(() => setAiDrafts(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, groupBy]);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/reports/owner-payout.${fmt}`, {
        params: { month, group_by: groupBy === "owner" ? "owner" : undefined },
        responseType: "blob",
      });
      const mime = fmt === "csv" ? "text/csv" : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newport-stays-payout-${month}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEmail = (row) => {
    setEmailTarget(row);
    setEmailAddr(row?.owner_email || "");
  };

  const sendPayout = async () => {
    if (!emailAddr || !emailAddr.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setEmailing(true);
    try {
      const { data: res } = await api.post("/reports/owner-payout/email", {
        to: emailAddr,
        month,
        group_by: groupBy === "owner" ? "owner" : null,
        owner_name: emailTarget?.owner_name || "",
      });
      if (res.ok) toast.success(`Payout PDF emailed to ${res.sent_to}`);
      else toast.error("Email failed — check the log");
      setEmailTarget(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEmailing(false);
    }
  };

  const openLink = (row) => {
    setLinkTarget(row);
    setLinkAddr(row?.owner_email || "");
  };

  const sendLoginLink = async () => {
    if (!linkAddr || !linkAddr.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setLinking(true);
    try {
      const { data: res } = await api.post("/landlord/send-link-admin", {
        owner_email: linkAddr,
      });
      if (res.ok) toast.success(`Owner portal link sent to ${res.sent_to}`);
      else toast.error("Send failed — check the email log");
      setLinkTarget(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLinking(false);
    }
  };

  const runMonthlyNow = async () => {
    if (!window.confirm(`Send this month's payout email to every landlord with activity in ${month}?`)) return;
    setRunningMonthly(true);
    try {
      const { data: res } = await api.post("/reports/run-monthly-payouts", { month });
      toast.success(`Monthly payouts: ${res.sent} sent · ${res.skipped} skipped`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRunningMonthly(false);
    }
  };

  const t = data?.totals || {};
  const isOwner = data?.group_by === "owner";

  return (
    <Layout
      title="Owner payout report"
      subtitle="Monthly gross, commissions and net per unit or per owner. PDF ready to email."
      actions={
        <>
          <Input
            data-testid={REPORTS.monthInput}
            type="month"
            className="h-11 w-44 bg-white"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
          <div className="inline-flex rounded-full border border-slate-200 bg-white overflow-hidden">
            <button
              data-testid="reports-group-property"
              onClick={() => setGroupBy("property")}
              className={`h-11 px-4 text-xs tracking-wider uppercase inline-flex items-center gap-1.5 ${
                groupBy === "property" ? "bg-teal-700 text-white" : "text-slate-600 hover:bg-stone-50"
              }`}
            >
              <Building2 className="w-3.5 h-3.5" /> By property
            </button>
            <button
              data-testid="reports-group-owner"
              onClick={() => setGroupBy("owner")}
              className={`h-11 px-4 text-xs tracking-wider uppercase inline-flex items-center gap-1.5 ${
                groupBy === "owner" ? "bg-teal-700 text-white" : "text-slate-600 hover:bg-stone-50"
              }`}
            >
              <Users className="w-3.5 h-3.5" /> By owner
            </button>
          </div>
          <Button
            data-testid={REPORTS.refreshBtn}
            variant="outline"
            onClick={load}
            disabled={busy}
            className="rounded-full h-11 px-5"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            data-testid="reports-preview-pdf"
            variant="outline"
            onClick={() => setPdfPreviewOpen(true)}
            className="rounded-full h-11 px-5"
            title="Preview the payout PDF inline before downloading"
          >
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button
            data-testid="reports-download-pdf"
            variant="outline"
            onClick={() => download("pdf")}
            className="rounded-full h-11 px-5"
          >
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button
            data-testid="reports-run-monthly"
            variant="outline"
            onClick={runMonthlyNow}
            disabled={runningMonthly}
            className="rounded-full h-11 px-5"
            title="Manually email every landlord their payout PDF+CSV for the selected month"
          >
            <Play className={`w-4 h-4 mr-2 ${runningMonthly ? "animate-pulse" : ""}`} />
            {runningMonthly ? "Sending…" : "Run monthly payouts"}
          </Button>
          <Button
            data-testid={REPORTS.downloadCsv}
            onClick={() => download("csv")}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
        </>
      }
    >
      <div data-testid={REPORTS.root} className="space-y-6">
        {preview && (
          <div
            data-testid="reports-payout-preview"
            className="ns-card p-5 border-l-4 border-teal-600 bg-teal-50/30"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <div className="ns-label text-teal-800">Auto monthly payouts · preview</div>
                  <div className="font-serif text-2xl mt-1 text-slate-900">
                    On the 1st, {preview.recipient_count} landlord{preview.recipient_count === 1 ? "" : "s"} will receive their statement
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    Total net payout <span className="font-medium text-slate-800">{money(preview.total_net)}</span> across {preview.total_properties} propert{preview.total_properties === 1 ? "y" : "ies"} for {preview.month}.
                    {preview.unassigned_bookings > 0 && (
                      <span className="text-amber-700"> · {preview.unassigned_bookings} booking{preview.unassigned_bookings === 1 ? "" : "s"} on units without an owner email — assign one to include them.</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="reports-preview-toggle"
                  variant="outline"
                  onClick={() => setPreviewOpen((o) => !o)}
                  className="rounded-full h-10 px-5 text-xs"
                  disabled={preview.recipient_count === 0}
                >
                  {previewOpen ? "Hide list" : "See who's getting it"}
                </Button>
              </div>
            </div>
            {previewOpen && preview.recipients.length > 0 && (
              <div className="mt-5 border-t border-teal-100 pt-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left px-2 py-2 ns-label">Landlord</th>
                      <th className="text-left px-2 py-2 ns-label">Email</th>
                      <th className="text-right px-2 py-2 ns-label">Bookings</th>
                      <th className="text-right px-2 py-2 ns-label">Gross</th>
                      <th className="text-right px-2 py-2 ns-label">Commission</th>
                      <th className="text-right px-2 py-2 ns-label">Net payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.recipients.map((r) => (
                      <tr key={r.owner_email} className="border-t border-teal-100/60">
                        <td className="px-2 py-2 font-medium">{r.owner_name || "Unnamed"}</td>
                        <td className="px-2 py-2 text-slate-500">{r.owner_email}</td>
                        <td className="px-2 py-2 text-right">{r.bookings}</td>
                        <td className="px-2 py-2 text-right">{money(r.gross)}</td>
                        <td className="px-2 py-2 text-right text-amber-700">{money(r.commission)}</td>
                        <td className="px-2 py-2 text-right font-medium">{money(r.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {upsells && (
          <div
            data-testid="reports-upsells-sales"
            className="ns-card p-5 border-l-4 border-amber-500 bg-amber-50/40"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                  <ShoppingBag className="w-5 h-5" />
                </div>
                <div>
                  <div className="ns-label text-amber-800">Upsells sold · {upsells.month}</div>
                  <div className="font-serif text-2xl mt-1 text-slate-900">
                    {upsells.total_items} extra{upsells.total_items === 1 ? "" : "s"} sold · {money(upsells.total_revenue)} revenue
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {upsells.unique_items > 0
                      ? `Top-earning right now: ${upsells.by_item[0]?.name} (${money(upsells.by_item[0]?.revenue)})`
                      : "No upsells sold yet this month — a nudge in the pre-arrival email might help."}
                  </div>
                </div>
              </div>
              <Button
                data-testid="reports-upsells-toggle"
                variant="outline"
                onClick={() => setUpsellsOpen((o) => !o)}
                className="rounded-full h-10 px-5 text-xs"
                disabled={upsells.total_items === 0}
              >
                {upsellsOpen ? "Hide detail" : "See breakdown"}
              </Button>
            </div>
            {upsells.forecast && upsells.forecast.days_remaining > 0 && (
              <div
                data-testid="reports-upsells-forecast"
                className="mt-4 pt-4 border-t border-amber-100 grid md:grid-cols-3 gap-4 text-sm"
              >
                <div>
                  <div className="ns-label text-amber-800 mb-1">Projected month-end</div>
                  <div className="font-serif text-2xl text-slate-900">{money(upsells.forecast.forecast_revenue)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    at {money(upsells.forecast.run_rate_per_day)}/day &middot; {upsells.forecast.days_remaining} day{upsells.forecast.days_remaining === 1 ? "" : "s"} left
                  </div>
                </div>
                <div>
                  <div className="ns-label text-amber-800 mb-1">Last month ({upsells.forecast.previous_month})</div>
                  <div className="font-serif text-2xl text-slate-900">{money(upsells.forecast.previous_total)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {upsells.forecast.previous_total > 0 ? "prior full month" : "no upsells sold last month"}
                  </div>
                </div>
                <div>
                  <div className="ns-label text-amber-800 mb-1">Pace</div>
                  {upsells.forecast.pace_status === "n/a" ? (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-slate-100 text-slate-600">
                      <Minus className="w-3.5 h-3.5" /> Nothing to compare yet
                    </div>
                  ) : upsells.forecast.pace_status === "ahead" ? (
                    <div
                      data-testid="upsells-pace-ahead"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-teal-50 text-teal-800 border border-teal-100"
                    >
                      <TrendingUp className="w-3.5 h-3.5" /> Ahead &middot; +{upsells.forecast.vs_previous_pct}%
                    </div>
                  ) : upsells.forecast.pace_status === "behind" ? (
                    <div
                      data-testid="upsells-pace-behind"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-100"
                    >
                      <TrendingDown className="w-3.5 h-3.5" /> Behind &middot; {upsells.forecast.vs_previous_pct}%
                    </div>
                  ) : (
                    <div
                      data-testid="upsells-pace-ontrack"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-200"
                    >
                      <Minus className="w-3.5 h-3.5" /> On track &middot; {upsells.forecast.vs_previous_pct >= 0 ? "+" : ""}{upsells.forecast.vs_previous_pct}%
                    </div>
                  )}
                </div>
              </div>
            )}
            {upsellsOpen && upsells.by_item.length > 0 && (
              <div className="mt-5 border-t border-amber-100 pt-4 grid md:grid-cols-2 gap-6">
                <div>
                  <div className="ns-label mb-2">By item</div>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left py-2 ns-label">Extra</th>
                        <th className="text-right py-2 ns-label">Sold</th>
                        <th className="text-right py-2 ns-label">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upsells.by_item.map((it) => (
                        <tr key={it.name} className="border-t border-amber-100/60">
                          <td className="py-2 font-medium">{it.name}</td>
                          <td className="py-2 text-right">{it.count}</td>
                          <td className="py-2 text-right font-medium">{money(it.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="ns-label mb-2">Recent orders</div>
                  <div className="space-y-1">
                    {upsells.orders.slice(0, 8).map((o, i) => (
                      <div key={i} className="text-sm flex items-center justify-between border-b border-amber-100/60 py-1.5">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{o.item}</div>
                          <div className="text-xs text-slate-500 truncate">{o.guest_name} · {o.property_name}</div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className="font-medium">{money(o.price)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {guidebook && guidebook.total_views > 0 && (
          <div
            data-testid="reports-guidebook-analytics"
            className="ns-card p-5 border-l-4 border-indigo-500 bg-indigo-50/40"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0 mt-0.5">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="ns-label text-indigo-800">Guidebook engagement &middot; {guidebook.period}</div>
                <div className="font-serif text-2xl mt-1 text-slate-900">
                  {guidebook.total_views} section view{guidebook.total_views === 1 ? "" : "s"} &middot; {guidebook.sections[0]?.label} leads
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Track which info guests actually hunt for once they arrive.
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {guidebook.sections.filter((s) => s.views > 0).map((s) => {
                    const pct = guidebook.total_views > 0 ? (s.views / guidebook.total_views) * 100 : 0;
                    return (
                      <div key={s.section} data-testid={`guidebook-${s.section}`} className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-slate-700 truncate">{s.label}</span>
                            <span className="text-slate-500 ml-3 shrink-0">{s.views} view{s.views === 1 ? "" : "s"} &middot; {s.unique_guests} guest{s.unique_guests === 1 ? "" : "s"}</span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-indigo-100 overflow-hidden">
                            <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {aiDrafts && aiDrafts.total_drafts > 0 && (
          <div data-testid="reports-ai-drafts" className="ns-card p-5 border-l-4 border-indigo-500 bg-indigo-50/30">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="ns-label text-indigo-800">AI drafts &middot; last 30 days</div>
                <div className="font-serif text-2xl mt-1 text-slate-900">
                  {aiDrafts.total_drafts} drafts &middot; <span className="font-mono">${aiDrafts.total_cost_usd.toFixed(4)}</span> spent
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  {aiDrafts.streamed_drafts} streamed &middot; {aiDrafts.tone_mix.length} tone{aiDrafts.tone_mix.length === 1 ? "" : "s"} in play
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <div className="ns-label mb-2">Tone mix</div>
                    <div className="space-y-1.5">
                      {aiDrafts.tone_mix.map((t) => {
                        const pct = aiDrafts.total_drafts > 0 ? (t.count / aiDrafts.total_drafts) * 100 : 0;
                        return (
                          <div key={t.tone} data-testid={`tone-${t.tone}`} className="flex items-center gap-3">
                            <span className="min-w-16 text-xs text-slate-600 capitalize">{t.tone}</span>
                            <div className="flex-1 h-1.5 rounded-full bg-indigo-100 overflow-hidden">
                              <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-slate-500 min-w-10 text-right">{t.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="ns-label mb-2">Top templates</div>
                    {aiDrafts.top_templates.length === 0 ? (
                      <div className="text-xs text-slate-400 italic">No template usage yet</div>
                    ) : (
                      <div className="space-y-1.5">
                        {aiDrafts.top_templates.slice(0, 6).map((t) => (
                          <div key={t.id} data-testid={`top-tpl-${t.id}`} className="flex items-center gap-3 text-xs">
                            <span className="flex-1 truncate text-slate-700">
                              {t.title}
                              {t.created_by_name && <span className="text-slate-400"> · {t.created_by_name}</span>}
                            </span>
                            <span className="font-mono text-slate-500">{t.usage_count}×</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="ns-label mb-2">Daily activity (30 days)</div>
                  <div className="flex items-end gap-1 h-12" data-testid="drafts-sparkline">
                    {aiDrafts.days.map((d) => {
                      const max = Math.max(1, ...aiDrafts.days.map((x) => x.drafts));
                      const h = Math.max(2, (d.drafts / max) * 48);
                      const anyEdits = d.edited > 0;
                      return (
                        <div
                          key={d.date}
                          className={`flex-1 rounded-sm ${anyEdits ? "bg-red-400" : d.drafts > 0 ? "bg-indigo-500" : "bg-slate-200"}`}
                          style={{ height: `${h}px` }}
                          title={`${d.date}: ${d.drafts} draft${d.drafts === 1 ? "" : "s"}${d.edited ? ` · ${d.edited} edited` : ""}`}
                        />
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Indigo = accepted verbatim, red = edited before sending &middot; hover for detail
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
          {[
            { label: "Bookings", value: t.bookings ?? 0 },
            { label: "Nights", value: t.nights ?? 0 },
            { label: "Gross", value: money(t.gross), tone: "teal" },
            { label: "Cleaning", value: money(t.cleaning) },
            { label: "Commission", value: money(t.commission), tone: "amber" },
            { label: "Net payout", value: money(t.net), tone: "navy" },
          ].map((s) => (
            <div key={s.label} className="ns-card p-5">
              <div className="ns-label">{s.label}</div>
              <div className={`font-serif text-3xl mt-3 ${s.tone === "teal" ? "text-teal-800" : s.tone === "amber" ? "text-amber-700" : "text-slate-900"}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="ns-card overflow-x-auto">
          <div className="p-5 flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-teal-700" />
            <div>
              <div className="ns-label">
                {isOwner ? "Per owner" : "Per unit"} · {data?.month}
              </div>
              <div className="text-xs text-slate-400">
                Commission rates:{" "}
                {Object.entries(data?.commissions || {})
                  .filter(([k]) => k)
                  .map(([k, v]) => `${CHANNEL_LABEL[k] || k} ${v}%`)
                  .join(" · ")}
              </div>
            </div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left px-6 py-3 ns-label">
                  {isOwner ? "Owner" : "Property"}
                </th>
                <th className="text-right px-4 py-3 ns-label">Bookings</th>
                <th className="text-right px-4 py-3 ns-label">Nights</th>
                <th className="text-right px-4 py-3 ns-label">Gross</th>
                <th className="text-right px-4 py-3 ns-label">Cleaning</th>
                <th className="text-right px-4 py-3 ns-label">Commission</th>
                <th className="text-right px-4 py-3 ns-label">Refunded</th>
                <th className="text-right px-6 py-3 ns-label">Net payout</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr
                  key={r.property_id || `${r.owner_name}-${i}`}
                  data-testid={REPORTS.row(r.property_id || r.owner_name || i)}
                  className={`border-t border-slate-100 ${r.bookings === 0 ? "opacity-50" : ""}`}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium">
                      {isOwner ? r.owner_name : r.property_name}
                    </div>
                    {isOwner ? (
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {(r.properties || []).slice(0, 4).join(" · ")}
                        {r.properties?.length > 4 ? ` +${r.properties.length - 4} more` : ""}
                        {r.owner_email && ` · ${r.owner_email}`}
                      </div>
                    ) : (
                      <>
                        {Object.keys(r.by_channel || {}).length > 0 && (
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {Object.entries(r.by_channel)
                              .map(([k, v]) => `${CHANNEL_LABEL[k] || k} ${money(v)}`)
                              .join(" · ")}
                          </div>
                        )}
                        {r.owner_name && r.owner_name !== "Unassigned" && (
                          <div className="text-[10px] tracking-widest uppercase text-slate-400 mt-0.5">
                            Owner: {r.owner_name}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">{r.bookings}</td>
                  <td className="px-4 py-4 text-right">{r.nights}</td>
                  <td className="px-4 py-4 text-right">{money(r.gross)}</td>
                  <td className="px-4 py-4 text-right">{money(r.cleaning)}</td>
                  <td className="px-4 py-4 text-right text-amber-700">{money(r.commission)}</td>
                  <td className="px-4 py-4 text-right text-red-600">
                    {r.refunded > 0 ? money(r.refunded) : "—"}
                  </td>
                  <td className="px-6 py-4 text-right font-medium">{money(r.net)}</td>
                  <td className="px-4 py-4">
                    {isOwner && r.bookings > 0 && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`reports-email-${i}`}
                          onClick={() => openEmail(r)}
                          className="text-slate-300 hover:text-teal-700 p-1.5"
                          title="Email payout PDF to this owner"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`reports-link-${i}`}
                          onClick={() => openLink(r)}
                          className="text-slate-300 hover:text-teal-700 p-1.5"
                          title="Send this owner a magic-link to their portal"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {data?.rows?.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-slate-400 text-sm italic">
                    No data for this month.
                  </td>
                </tr>
              )}
            </tbody>
            {data?.rows?.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-stone-50/60">
                  <td className="px-6 py-3 font-serif text-lg">Total</td>
                  <td className="px-4 py-3 text-right font-medium">{t.bookings}</td>
                  <td className="px-4 py-3 text-right font-medium">{t.nights}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(t.gross)}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(t.cleaning)}</td>
                  <td className="px-4 py-3 text-right font-medium text-amber-700">
                    {money(t.commission)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-red-600">
                    {t.refunded > 0 ? money(t.refunded) : "—"}
                  </td>
                  <td className="px-6 py-3 text-right font-serif text-lg text-slate-900">
                    {money(t.net)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <Dialog open={!!emailTarget} onOpenChange={(v) => !v && setEmailTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Email payout PDF</DialogTitle>
          </DialogHeader>
          {emailTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-500">
                Sending {emailTarget.owner_name}'s payout for {data?.month} — net{" "}
                <span className="font-medium text-slate-800">{money(emailTarget.net)}</span>
              </div>
              <div>
                <Label className="ns-label">To</Label>
                <Input
                  data-testid="reports-email-to"
                  type="email"
                  className="mt-2 h-11 bg-white"
                  value={emailAddr}
                  onChange={(e) => setEmailAddr(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button
              data-testid="reports-email-send"
              onClick={sendPayout}
              disabled={emailing}
              className="rounded-full ns-btn-primary px-6"
            >
              {emailing ? "Sending…" : "Send PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkTarget} onOpenChange={(v) => !v && setLinkTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Send owner portal link</DialogTitle>
          </DialogHeader>
          {linkTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-500">
                Emails {linkTarget.owner_name || "the owner"} a secure magic-link that logs
                them into their read-only owner portal. Link expires in 24 hours.
              </div>
              <div>
                <Label className="ns-label">To</Label>
                <Input
                  data-testid="reports-link-to"
                  type="email"
                  className="mt-2 h-11 bg-white"
                  value={linkAddr}
                  onChange={(e) => setLinkAddr(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkTarget(null)}>Cancel</Button>
            <Button
              data-testid="reports-link-send"
              onClick={sendLoginLink}
              disabled={linking}
              className="rounded-full ns-btn-primary px-6"
            >
              {linking ? "Sending…" : "Send login link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        endpoint={pdfPreviewOpen ? "/reports/owner-payout.pdf" : null}
        params={{ month, group_by: groupBy === "owner" ? "owner" : undefined }}
        filename={`newport-stays-payout-${month}.pdf`}
        title={`Owner payout · ${month}${groupBy === "owner" ? " · by owner" : " · by property"}`}
      />
    </Layout>
  );
}
